import httpRequest from "~/composables/requset";
import type {ResponseData} from '~/utils/types';

// 使用者送出場地詢問。captcha_token 為 Cloudflare Turnstile token。
// data.broadcast=true 時 fan-out 至同縣市同類型的其他場地（Phase 2）。
export function createInquiry(data): Promise<ResponseData> {
    return httpRequest('POST', '/api/inquiries', data)
}

// 廣播配對預覽：回傳符合的「類似場地」數量（不含主場地，已套用收件人上限）。
// query 直接放進 URL —— 同 getInquiryOverview 的理由（GET params 會被雙層包裝）。
export function getInquiryMatchCount(spaceId: number, guestCount?: number): Promise<ResponseData> {
    const guest = guestCount === undefined ? '' : `&guest_count=${guestCount}`
    return httpRequest('GET', `/api/inquiry-match-count?space_id=${spaceId}${guest}`)
}

// 場地主以 reply token 讀取回覆頁內容
export function getInquiryReply(token: string): Promise<ResponseData> {
    return httpRequest('GET', `/api/inquiry-replies/${token}`)
}

// A3b 入口③/⑤:免登入以 reply token 換一枚一次性 LINE 綁定碼(後端解析 admin)。
// 回傳 {code, deep_link, expires_in_sec};場地主掃 QR/開 OA 送出即綁定。
export function mintLineBindCode(token: string): Promise<ResponseData> {
    return httpRequest('POST', `/api/inquiry-replies/${token}/line-bind-code`, {})
}

// 場地主以 reply token 送出回覆,可選帶報價(日期/時數/金額)。
// 帶 quote 時,回覆與報價在後端同一 transaction 一起寫入(原子,不會只寫到一半)。
export function submitInquiryReply(
    token: string,
    message: string,
    quote?: {date: string; hours: number; amount: number; valid_days?: number}
): Promise<ResponseData> {
    const body = quote ? {message, quote} : {message}
    return httpRequest('POST', `/api/inquiry-replies/${token}`, body)
}

// 使用者以 access token 查看詢問總覽。
// token 直接放進 URL query —— httpRequest 的 GET 分支會把 data 包成 {params: data}
// 再指派給 options.params（雙層），導致一般 data 帶不出 query；放進 URL 可繞過。
export function getInquiryOverview(id: number | string, token: string): Promise<ResponseData> {
    return httpRequest('GET', `/api/inquiries/${id}?token=${encodeURIComponent(token)}`)
}

// 租客在站內 thread 回貼訊息（sender_type='user'）。token 放 URL query，同上理由。
export function submitUserMessage(inquiryId: number | string, recipientId: number | string, token: string, message: string): Promise<ResponseData> {
    return httpRequest('POST', `/api/inquiries/${inquiryId}/recipients/${recipientId}/messages?token=${encodeURIComponent(token)}`, {message})
}

// 租客自我回報「成交了」軟訊號。token 放 URL query，同上理由。
export function reportDealClosed(inquiryId: number | string, token: string): Promise<ResponseData> {
    return httpRequest('POST', `/api/inquiries/${inquiryId}/close?token=${encodeURIComponent(token)}`, {})
}

// 結構化報價內容（存進 inquiry_messages.message JSON）。
export interface QuoteBody {
    v: number
    date: string
    hours: number
    amount: number
    // PF P1：報價有效期限（RFC3339）。缺此欄＝舊 v1 報價，永不過期。
    expires_at?: string
}

// 場地主以 reply token 在 thread 內送結構化報價（日期/時數/金額）。
export function submitInquiryQuote(token: string, quote: {date: string; hours: number; amount: number; valid_days?: number}): Promise<ResponseData> {
    return httpRequest('POST', `/api/inquiry-replies/${token}/quote`, quote)
}

// 場地主撤回最新報價（PF P1 Slice 3）：撤回後租客不可接受，可再重新報價。無 body。
export function withdrawInquiryQuote(token: string): Promise<ResponseData> {
    return httpRequest('POST', `/api/inquiry-replies/${token}/withdraw-quote`, {})
}

// 「我的詢問與報價中心」清單用型別（RR P2）。quote 已由後端解析為扁平物件，
// acceptable=true 代表現在可被接受（未過期且詢問未結案）。
export interface MyInquiryQuote {
    amount: number
    hours: number
    date: string
    expires_at?: string
    acceptable: boolean
    // PF P1 Slice 3：場地主已撤回此報價（仍顯示數字，但不可接受）。
    withdrawn: boolean
}

export interface MyInquiryRecipient {
    recipient_id: number
    space_id: number
    space_name: string
    status: string
    has_quote: boolean
    quote: MyInquiryQuote | null
}

export interface MyInquiry {
    id: number
    message: string
    status: 'open' | 'replied' | 'closed'
    created_at: string
    target_type: 'space' | 'broadcast'
    user_access_token: string
    recipients: MyInquiryRecipient[]
}

// 租客登入後查看自己的詢問清單（JWT bearer，無 body；同 getFavorites 用法）。
export async function getMyInquiries(): Promise<MyInquiry[]> {
    const data = await httpRequest<{inquiries: MyInquiry[]}>('GET', '/api/inquiries')
    return data?.inquiries ?? []
}

// 租客接受指定 recipient 的報價 → 觸發成交（建單 + 結案）。
// token 放 URL query，同 submitUserMessage 理由；金額/日期由後端讀 message 決定，空 body。
export function acceptQuote(inquiryId: number | string, recipientId: number | string, token: string): Promise<ResponseData> {
    return httpRequest('POST', `/api/inquiries/${inquiryId}/recipients/${recipientId}/accept-quote?token=${encodeURIComponent(token)}`, {})
}
